import { useState } from 'react';
import { useApp } from '../App';
import BackButton from '../components/BackButton';
import { useI18n } from '../i18n/index.jsx';

export default function QRPayment() {
  const { user, currentCompany } = useApp();
  const { t } = useI18n();
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('PLN');
  const [description, setDescription] = useState('');
  const [qrCode, setQrCode] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [paymentLink, setPaymentLink] = useState(null);
  const [error, setError] = useState(null);

  async function generateQR() {
    setError(null);
    
    if (!amount || parseFloat(amount) <= 0) {
      setError('Введите сумму');
      return;
    }
    
    setGenerating(true);
    
    try {
      // Create simple payment data
      const data = `PAY:${amount}:${currency}:${Date.now()}`;
      const link = `https://workifypl.com/pay?a=${amount}&c=${currency}&d=${encodeURIComponent(description || 'Payment')}`;
      setPaymentLink(link);
      
      // Use quickchart.io - most reliable QR service
      const qrUrl = `https://quickchart.io/qr?text=${encodeURIComponent(link)}&size=300&margin=2`;
      
      // Set QR directly without preloading
      setQrCode(qrUrl);
      setGenerating(false);
      
    } catch (e) {
      console.error('QR generation error:', e);
      setError('Ошибка генерации QR: ' + e.message);
      setGenerating(false);
    }
  }

  function copyLink() {
    if (paymentLink) {
      navigator.clipboard.writeText(paymentLink);
    }
  }

  function shareQR() {
    if (paymentLink && navigator.share) {
      navigator.share({
        title: `Оплата ${amount} ${currency}`,
        text: description || 'Сканируйте QR для оплаты',
        url: paymentLink,
      });
    } else {
      copyLink();
    }
  }

  function downloadQR() {
    if (qrCode) {
      const a = document.createElement('a');
      a.href = qrCode;
      a.download = `qr_payment_${amount}_${currency}.png`;
      a.target = '_blank';
      a.click();
    }
  }

  function reset() {
    setQrCode(null);
    setPaymentLink(null);
    setAmount('');
    setDescription('');
  }

  return (
    <div className="page">
      <BackButton />
      <h1>📱 QR-код для оплаты</h1>

      {/* Info */}
      <div style={{
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: 4 }}>💡 Быстрый приём платежей</div>
        <div style={{ fontSize: 13, opacity: 0.9 }}>
          Создайте QR-код и покажите клиенту — он сканирует и оплачивает
        </div>
      </div>

      {!qrCode ? (
        /* Input Form */
        <div style={{
          background: 'rgba(255,255,255,0.05)',
          borderRadius: 12,
          padding: 20,
        }}>
          {/* Amount */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, opacity: 0.8 }}>{t('common.amount')}</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="100"
                style={{
                  flex: 1,
                  padding: 16,
                  borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.05)',
                  color: '#fff',
                  fontSize: 20,
                }}
              />
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                style={{
                  width: 90,
                  padding: 16,
                  borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.05)',
                  color: '#fff',
                }}
              >
                <option value="PLN">PLN</option>
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
                <option value="USDT">USDT</option>
              </select>
            </div>
          </div>

          {/* Description */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, opacity: 0.8 }}>Описание (опционально)</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="За услугу..."
              style={{
                width: '100%',
                padding: 12,
                borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.05)',
                color: '#fff',
              }}
            />
          </div>

          {/* Error */}
          {error && (
            <div style={{
              background: 'rgba(239,68,68,0.2)',
              borderRadius: 8,
              padding: 12,
              marginBottom: 16,
              textAlign: 'center',
              color: '#ef4444',
            }}>
              {error}
            </div>
          )}

          {/* Generate Button */}
          <button
            className="btn btn-primary"
            style={{ width: '100%', padding: 16, fontSize: 16 }}
            onClick={generateQR}
            disabled={generating}
          >
            {generating ? '⏳ Генерация...' : '📱 Сгенерировать QR-код'}
          </button>
        </div>
      ) : (
        /* QR Code Display */
        <div style={{
          background: 'rgba(255,255,255,0.05)',
          borderRadius: 12,
          padding: 20,
          textAlign: 'center',
        }}>
          {/* Amount Display */}
          <div style={{
            fontSize: 32,
            fontWeight: 'bold',
            color: '#10b981',
            marginBottom: 16,
          }}>
            {amount} {currency}
          </div>

          {description && (
            <div style={{ opacity: 0.7, marginBottom: 16 }}>
              {description}
            </div>
          )}

          {/* QR Code Image */}
          <div style={{
            background: '#fff',
            borderRadius: 16,
            padding: 20,
            display: 'inline-block',
            marginBottom: 16,
          }}>
            <img 
              src={qrCode} 
              alt="QR код"
              style={{ 
                width: 250, 
                height: 250,
                display: 'block',
              }}
              onError={(e) => {
                // If image fails, show fallback
                e.target.src = `https://quickchart.io/qr?text=${encodeURIComponent(paymentLink)}&size=250`;
              }}
            />
          </div>

          {/* Payment Link */}
          <div style={{
            background: 'rgba(0,0,0,0.3)',
            borderRadius: 8,
            padding: 12,
            marginBottom: 16,
            wordBreak: 'break-all',
            fontSize: 11,
            opacity: 0.7,
          }}>
            {paymentLink}
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <button
              className="btn"
              style={{ flex: 1, background: 'rgba(16,185,129,0.3)' }}
              onClick={shareQR}
            >
              📤 Поделиться
            </button>
            <button
              className="btn"
              style={{ flex: 1, background: 'rgba(99,102,241,0.3)' }}
              onClick={copyLink}
            >
              📋 Копировать
            </button>
          </div>

          <button
            className="btn"
            style={{ width: '100%', background: 'rgba(255,255,255,0.1)' }}
            onClick={downloadQR}
          >
            💾 Скачать QR
          </button>

          <button
            style={{
              width: '100%',
              marginTop: 16,
              padding: 12,
              background: 'transparent',
              border: 'none',
              color: 'rgba(255,255,255,0.5)',
              cursor: 'pointer',
            }}
            onClick={reset}
          >
            ← Создать новый
          </button>
        </div>
      )}

      {/* Recent QR codes (optional) */}
      <div style={{
        background: 'rgba(255,255,255,0.05)',
        borderRadius: 12,
        padding: 16,
        marginTop: 16,
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: 8 }}>💡 Подсказка</div>
        <div style={{ fontSize: 13, opacity: 0.7 }}>
          QR-код содержит ссылку на оплату. Клиент может:
          <ul style={{ paddingLeft: 20, margin: '8px 0' }}>
            <li>Сканировать камерой телефона</li>
            <li>Перейти по ссылке</li>
            <li>Оплатить удобным способом</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
